import { useState, useEffect, useCallback } from 'react'

/**
 * Hook for managing subscription data and state
 * Handles fetching user subscription information and managing subscription status
 */
const useSubscription = () => {
  const [userId, setUserId] = useState('')
  const [stripeCustomerId, setStripeCustomerId] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)

  /**
   * Fetches subscription information for a given username
   * @param {string} username - The username to fetch subscription info for
   * @returns {Object|string} User object with subscription data or error message
   */
  const getSubscriptionInfo = useCallback(async username => {
    const API_GATEWAY_URL = process.env.REACT_APP_API_GATEWAY_URL
    let user

    try {
      if (username) {
        console.log('getSubscriptionInfo:', username)
        const userRequest = await fetch(
          `${API_GATEWAY_URL}/k1/users/${username}`
        )

        if (!userRequest.ok) {
          throw new Error(`HTTP error! status: ${userRequest.status}`)
        }

        user = await userRequest.json()
      }
    } catch (e) {
      console.error('Error fetching subscription info:', e)
      return e.message
    }

    return user
  }, [])

  /**
   * Fetches subscription information for a specific user ID
   */
  const fetchSubscriptionInfo = useCallback(
    async userIdToFetch => {
      if (!userIdToFetch) return

      try {
        setIsLoading(true)
        setError(null)

        const user = await getSubscriptionInfo(userIdToFetch)

        if (typeof user === 'string') {
          // Error case - user is error message
          setError(user)
          return
        }

        if (user?.subscription?.stripeId) {
          setStripeCustomerId(user.subscription.stripeId)
        } else {
          setStripeCustomerId(null)
        }
      } catch (e) {
        console.error('Error in fetchSubscriptionInfo:', e)
        setError(e.message)
      } finally {
        setIsLoading(false)
      }
    },
    [getSubscriptionInfo]
  )

  /**
   * Initialize user ID from localStorage on mount
   */
  useEffect(() => {
    const userid = localStorage.getItem('username')
    if (userid) {
      setUserId(userid)
    }
  }, [])

  /**
   * Fetch subscription info when userId changes
   */
  useEffect(() => {
    if (userId) {
      fetchSubscriptionInfo(userId)
    }
  }, [userId, fetchSubscriptionInfo])

  /**
   * Determines if user has an active subscription
   * @returns {boolean} True if user has stripe customer ID
   */
  const hasActiveSubscription = useCallback(() => {
    return !!stripeCustomerId
  }, [stripeCustomerId])

  /**
   * Refresh subscription data
   */
  const refreshSubscription = useCallback(() => {
    if (userId) {
      fetchSubscriptionInfo(userId)
    }
  }, [userId, fetchSubscriptionInfo])

  return {
    // State
    userId,
    stripeCustomerId,
    isLoading,
    error,

    // Computed values
    hasActiveSubscription: hasActiveSubscription(),

    // Actions
    getSubscriptionInfo,
    fetchSubscriptionInfo,
    refreshSubscription,

    // Setters
    setUserId,
    setStripeCustomerId,
    setError
  }
}

export default useSubscription
